/*
 * Organic — the behaviours the design needs that the theme has no
 * equivalent for: the seasonal hero switcher, the live name preview, the
 * name drawn onto a product photo and the handoff of the typed name from
 * the homepage teaser to its product.
 *
 * The elements follow the theme's custom-element convention from `assets/global.js`:
 * guard the definition, keep the element usable before JS lands.
 */

if (!customElements.get('organic-season-switcher')) {
  customElements.define(
    'organic-season-switcher',
    class OrganicSeasonSwitcher extends HTMLElement {
      connectedCallback() {
        this.buttons = Array.from(this.querySelectorAll('[data-season-target]'));
        if (!this.buttons.length) return;

        this.panels = Array.from(this.querySelectorAll('[data-season-panel]'));

        /* One delegated listener rather than one per chip, so the theme editor
           reconnecting this element cannot stack a second handler on every
           button. */
        this.onClick = this.onClick.bind(this);
        this.addEventListener('click', this.onClick);

        const active = this.buttons.find((button) => button.getAttribute('aria-selected') === 'true');
        this.select(active ? active.dataset.seasonTarget : this.buttons[0].dataset.seasonTarget);
      }

      disconnectedCallback() {
        if (this.onClick) this.removeEventListener('click', this.onClick);
      }

      onClick(event) {
        const button = event.target.closest('[data-season-target]');
        if (button) this.select(button.dataset.seasonTarget);
      }

      select(season) {
        this.buttons.forEach((button) => {
          button.setAttribute('aria-selected', String(button.dataset.seasonTarget === season));
        });
        this.panels.forEach((panel) => {
          panel.hidden = panel.dataset.seasonPanel !== season;
        });
      }
    }
  );
}

if (!customElements.get('organic-name-preview')) {
  customElements.define(
    'organic-name-preview',
    class OrganicNamePreview extends HTMLElement {
      connectedCallback() {
        /* `data-name-input` stays the explicit opt-in, used by the homepage
           teaser. On the product page the fields are rendered by
           product-custom-fields, which knows nothing about this element, so
           fall back to the first text field — the name on every product that
           has one. Without the fallback the caption would never update. */
        const explicit = this.querySelector('[data-name-input]');
        this.input = explicit || this.querySelector('input[type="text"]');
        if (!this.input) return;

        /* The product page renders the caption inside the media column while
           the input sits in the info column, so the output is addressed by
           selector rather than looked up inside the element. Resolved once —
           `onInput` runs on every keystroke. */
        const targets = this.getAttribute('targets');
        this.outputs = targets ? Array.from(document.querySelectorAll(targets)) : [];

        this.counter = this.querySelector('[data-name-counter]');
        this.maxLength = Number(this.input.getAttribute('maxlength')) || 0;

        /* Only for the delegated markup, which carries no counter — the gate is
           `!explicit`, because the homepage teaser marks its own input and lays
           it out in a flex row where an injected sibling would become a third
           item. Tagged with the attribute it is looked up by, so the theme
           editor reconnecting this node finds it rather than adding a second.
           No maxlength configured means no counter, which is the honest state. */
        if (!explicit && !this.counter && this.maxLength) {
          const meta = document.createElement('div');
          meta.className = 'organic-personalization__meta';

          const hint = this.getAttribute('hint');
          if (hint) {
            const label = document.createElement('span');
            label.textContent = hint.split('{count}').join(this.maxLength);
            meta.appendChild(label);
          }

          this.counter = document.createElement('span');
          this.counter.setAttribute('data-name-counter', '');
          meta.appendChild(this.counter);

          (this.input.closest('.field') || this.input).insertAdjacentElement('afterend', meta);
        }

        this.onInput = this.onInput.bind(this);
        this.input.addEventListener('input', this.onInput);
        this.onInput();
      }

      /* The theme editor disconnects and reconnects a section's nodes on every
         edit, so drop the listener rather than stacking a second one. */
      disconnectedCallback() {
        if (this.input) this.input.removeEventListener('input', this.onInput);
      }

      onInput() {
        const raw = (this.input.value || '').trim();
        const name = this.hasAttribute('uppercase') ? raw.toUpperCase() : raw;
        const template = this.getAttribute('template') || '{name}';
        const empty = this.getAttribute('empty') || '';
        /* split/join, not `replace`: a string replacement expands `$&`, `$'` and
           `` $` `` — so a customer typing `$&` would print `{name}` back — and
           `replace` only ever substitutes the first token. */
        const line = name ? template.split('{name}').join(name) : empty;

        this.outputs.forEach((output) => {
          output.textContent = line;
        });

        if (this.counter && this.maxLength) {
          this.counter.textContent = `${(this.input.value || '').length}/${this.maxLength}`;
        }
      }
    }
  );
}

/* Name on the photo. `snippets/organic-name-photo.liquid` renders a photo
   saved without the name and an SVG over it; this writes the typed name into
   the SVG at the print's size and moves the gold rules beside it. The
   geometry is measured on the shop photos; what to do with a name the print
   was not laid out for is decided here: the rules keep their length and gap
   until the group would outgrow the print, then shorten (down to a third),
   and only then does the type shrink. */
if (!customElements.get('organic-name-photo')) {
  const FONT = "'Organic Print Cormorant'";

  customElements.define(
    'organic-name-photo',
    class OrganicNamePhoto extends HTMLElement {
      connectedCallback() {
        try {
          this.geometry = JSON.parse(this.getAttribute('geometry'));
        } catch (error) {
          return;
        }
        this.text = this.querySelector('[data-name-text]');
        this.rules = Array.from(this.querySelectorAll('[data-name-rule]'));
        if (!this.text || this.rules.length !== 2) return;

        const selector = this.getAttribute('input');
        this.input = selector ? document.querySelector(selector) : null;

        this.render = this.render.bind(this);
        if (this.input) this.input.addEventListener('input', this.render);

        /* The photo follows the colour option. The theme writes the chosen
           variant into the form's `id` input in its own change handler, which
           runs before this document-level one. */
        const formId = this.getAttribute('form-id');
        const variants = this.querySelector('[data-name-variants]');
        if (formId && variants) {
          try {
            this.colours = new Map(JSON.parse(variants.textContent).map((variant) => [String(variant.id), variant.colour]));
          } catch (error) {
            this.colours = null;
          }
          this.form = document.getElementById(formId);
          this.onChange = this.showColour.bind(this);
          document.addEventListener('change', this.onChange);
        }

        /* Measuring before the font is in would place the rules for the
           fallback serif. `font-display: block` keeps the name invisible
           meanwhile, so nothing wrong is ever on screen. */
        this.ready = false;
        document.fonts
          .load(`700 100px ${FONT}`)
          .then(() => {
            this.ready = true;
            this.render();
          })
          .catch(() => {});
        this.render();
        this.watchPhoto();
      }

      disconnectedCallback() {
        if (this.input) this.input.removeEventListener('input', this.render);
        if (this.onChange) document.removeEventListener('change', this.onChange);
      }

      name() {
        const typed = this.input ? (this.input.value || '').trim() : '';
        const shown = typed || this.getAttribute('placeholder') || this.geometry.example;
        return shown.toLocaleUpperCase('de-DE');
      }

      /* Left and right edge of the ink, relative to the centre of the advance
         box the SVG anchors at with `text-anchor="middle"`, at type size `size`. */
      ink(name, size) {
        if (!this.context) this.context = document.createElement('canvas').getContext('2d');
        const context = this.context;
        context.font = `700 1000px ${FONT}`;
        context.textAlign = 'center';
        if ('fontKerning' in context) context.fontKerning = 'normal';
        const metrics = context.measureText(name);
        return [(-metrics.actualBoundingBoxLeft * size) / 1000, (metrics.actualBoundingBoxRight * size) / 1000];
      }

      render() {
        const name = this.name();
        this.text.textContent = name;
        if (!this.ready) return;

        const g = this.geometry;
        const rule = g.rule;
        let size = g.s;
        let [left, right] = this.ink(name, size);
        let length = rule.length;

        const spare = g.maxWidth - (right - left) - 2 * rule.gap;
        if (spare < 2 * rule.length) {
          length = Math.max(rule.length / 3, spare / 2);
          if (spare / 2 < rule.length / 3) {
            size *= (g.maxWidth - 2 * (rule.gap + length)) / (right - left);
            [left, right] = this.ink(name, size);
          }
        }

        this.text.setAttribute('font-size', size.toFixed(2));
        this.text.setAttribute('stroke-width', ((2 * g.outline * size) / g.s).toFixed(2));

        [-1, 1].forEach((side, index) => {
          const inner = g.cx + (side < 0 ? left : right) + side * rule.gap;
          const outer = inner + side * length;
          const cy = rule.cy[index];
          const top = cy - rule.thickness / 2;
          const bottom = cy + rule.thickness / 2;
          let points;
          if (rule.tip[index] === 'outer') {
            const tip = outer - side * rule.taper;
            points = [[inner, top], [tip, top], [outer, cy], [tip, bottom], [inner, bottom]];
          } else {
            const tip = inner + side * rule.taper;
            points = [[inner, cy], [tip, top], [outer, top], [outer, bottom], [tip, bottom]];
          }
          this.rules[index].setAttribute('points', points.map((point) => point.map((v) => v.toFixed(2)).join(',')).join(' '));
        });
      }

      showColour() {
        if (!this.colours || !this.form) return;
        const id = this.form.querySelector('input[name="id"]');
        const colour = id ? this.colours.get(String(id.value)) : null;
        if (!colour) return;

        const slides = Array.from(this.querySelectorAll('[data-colour]'));
        /* A colour without its own photo keeps the current one rather than
           showing another colour under the customer's choice. */
        if (!slides.some((slide) => slide.dataset.colour === colour)) return;
        slides.forEach((slide) => {
          slide.hidden = slide.dataset.colour !== colour;
        });
        this.watchPhoto();
      }

      visibleImage() {
        return this.querySelector('.organic-name-photo__frame > img, [data-colour]:not([hidden]) img');
      }

      /* The name waits for its photo: on the empty frame it would be the print
         on nothing. `complete` is also true for a photo that failed, which
         then shows the name rather than hiding it for good. */
      watchPhoto() {
        const image = this.visibleImage();
        if (!image) return;

        if (image.complete) {
          this.removeAttribute('loading-photo');
          /* The other colours load behind the one shown, so a colour switch
             does not leave the frame empty: a hidden lazy image would wait
             until it is shown. */
          this.querySelectorAll('[data-colour][hidden] img[loading="lazy"]').forEach((other) => {
            other.loading = 'eager';
          });
          return;
        }

        this.setAttribute('loading-photo', '');
        const done = () => {
          if (this.visibleImage() === image) this.watchPhoto();
        };
        image.addEventListener('load', done, { once: true });
        image.addEventListener('error', done, { once: true });
      }
    }
  );
}

/* Name handoff. The homepage teaser's CTA links to one product, and the name
   typed into the teaser goes along into that product's name field, so nobody
   types it twice.

   sessionStorage rather than a `?name=` query: a child's name in the address
   would reach every analytics pixel together with the page URL. The entry
   names the page it was meant for and is read once, so a name typed for one
   product never turns up prefilled on another. */
if (!window.organicNameHandoff) {
  window.organicNameHandoff = true;

  const storageKey = 'organic-name-handoff';

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[data-name-handoff]');
    if (!link) return;

    const preview = link.closest('organic-name-preview');
    const input = preview ? preview.querySelector('[data-name-input]') : null;
    const name = input ? input.value.trim() : '';

    try {
      if (name) {
        sessionStorage.setItem(storageKey, JSON.stringify({ name, path: new URL(link.href).pathname }));
      } else {
        sessionStorage.removeItem(storageKey);
      }
    } catch (error) {
      /* Storage blocked (private mode, strict settings): the link still
         works, the field on the product page just stays empty. */
    }
  });

  let handoff = null;
  try {
    handoff = JSON.parse(sessionStorage.getItem(storageKey));
  } catch (error) {
    /* Unreadable storage or a malformed entry: nothing to hand over. */
  }

  if (handoff && handoff.path === window.location.pathname) {
    sessionStorage.removeItem(storageKey);

    /* The first enabled text field is the name, the same rule
       organic-name-preview falls back to on the product page. A field held
       back by a `nur_bei` rule is disabled, so it is skipped, and a value the
       browser restored on a back navigation is left alone. */
    const field = document.querySelector('.product-custom-fields input[type="text"]:not([disabled])');
    if (field && !field.value) {
      field.value = handoff.name;
      field.dispatchEvent(new Event('input', { bubbles: true }));
      field.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}


/* Collection text on phones. A long collection description used to fill the
   whole first screen on a phone, so the products started below the fold.
   `sections/main-collection-banner.liquid` marks a description with at least
   30 words after its first paragraph as collapsible, `organic.css` then shows
   only that first paragraph below 750px, and this button opens the rest. The
   text stays in the HTML either way, so search engines read all of it. */
if (!window.organicCollectionMore) {
  window.organicCollectionMore = true;

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-collection-more]');
    if (!button) return;

    const text = document.getElementById(button.getAttribute('aria-controls'));
    if (!text) return;

    const open = text.classList.toggle('is-open');
    button.setAttribute('aria-expanded', String(open));

    const label = button.querySelector('[data-collection-more-label]');
    if (label) label.textContent = open ? button.dataset.labelLess : button.dataset.labelMore;
  });
}


/* Staffelpreise for the B2B pens and mugs, in place of the PriceBazi app.
   `snippets/staffelpreise.liquid` renders the tier table from the metafield
   `custom.staffelpreise`, and its first tier is the minimum order. This element
   keeps the quantity field at or above that minimum, marks the tier that applies
   and writes the total under the field. The cheaper price itself is taken off by
   Shopify's automatic discounts, so nothing here touches the cart. The field sits
   in `.product-parameters` next to this element, so it is looked up through the
   parent — the quick view renders the same markup. */
if (!customElements.get('staffel-preise')) {
  customElements.define(
    'staffel-preise',
    class StaffelPreise extends HTMLElement {
      connectedCallback() {
        this.tiers = JSON.parse(this.dataset.staffeln || '[]');
        this.input = this.parentElement.querySelector('input[name="quantity"]');
        if (!this.tiers.length || !this.input) return;

        this.total = this.parentElement.querySelector('.staffelpreise__summe');

        /* `input` only updates the display, so a customer typing 150 is not
           corrected at the 1. `change` — leaving the field, or the plus and minus
           buttons — lifts anything below the minimum, before the form is sent. */
        this.onInput = this.update.bind(this);
        this.onChange = () => {
          this.clamp();
          this.update();
        };
        this.input.addEventListener('input', this.onInput);
        this.input.addEventListener('change', this.onChange);
        this.onChange();
      }

      /* The theme editor disconnects and reconnects a section's nodes on every
         edit, so drop the listeners rather than stacking a second pair. */
      disconnectedCallback() {
        if (!this.input) return;
        this.input.removeEventListener('input', this.onInput);
        this.input.removeEventListener('change', this.onChange);
      }

      clamp() {
        const minimum = this.tiers[0][0];
        if (!(parseInt(this.input.value, 10) >= minimum)) this.input.value = minimum;
      }

      update() {
        const quantity = parseInt(this.input.value, 10);
        if (!(quantity >= this.tiers[0][0])) return;

        const [from, cents] = this.tiers.filter(([tierFrom]) => quantity >= tierFrom).pop();
        this.querySelectorAll('tr[data-ab]').forEach((row) => {
          row.classList.toggle('is-aktiv', Number(row.dataset.ab) === from);
        });

        if (!this.total) return;
        const price = (amount) => this.formatMoney(amount);
        const saved = (this.tiers[0][1] - cents) * quantity;
        this.total.textContent =
          `${quantity} Stück × ${price(cents)} = ${price(cents * quantity)}` +
          (saved > 0 ? ` · Ersparnis ${price(saved)}` : '');
        this.total.hidden = false;
      }

      /* In the shop's own money format (`theme.moneyFormat` from layout/theme.liquid,
         `€{{amount_with_comma_separator}}` here), so the total reads like the prices
         the Liquid `money` filter prints around it. */
      formatMoney(cents) {
        const format = (window.theme && window.theme.moneyFormat) || '€{{amount_with_comma_separator}}';
        return format.replace(/<[^>]*>/g, '').replace(/\{\{\s*(\w+)\s*\}\}/, (match, kind) => {
          const separators = [['period_and_space', ' ', '.'], ['comma', '.', ','], ['space', ' ', ','], ['apostrophe', "'", '.']];
          const [, group, point] = separators.find(([name]) => kind.includes(name)) || [null, ',', '.'];
          const [whole, fraction] = (cents / 100).toFixed(kind.includes('no_decimals') ? 0 : 2).split('.');
          return whole.replace(/\B(?=(\d{3})+(?!\d))/g, group) + (fraction ? point + fraction : '');
        });
      }
    }
  );
}


/* Cart below a minimum order. `snippets/staffelpreise-warenkorb.liquid` prints a
   warning while a product with Staffelpreise is below its first tier, and the
   Liquid then renders „Zur Kasse“ disabled. The cart drawer re-renders whole on
   every change, but the cart page only swaps the subtotal block (`.js-contents`),
   which leaves its checkout button as it was — so here it follows the warning. */
if (!window.staffelpreiseCheckout) {
  window.staffelpreiseCheckout = true;

  const footer = document.getElementById('main-cart-footer');
  const checkout = footer && footer.querySelector('.cart__checkout-button');

  if (checkout) {
    new MutationObserver(() => {
      checkout.disabled =
        footer.classList.contains('is-empty') || Boolean(footer.querySelector('[data-staffel-warnung]'));
    }).observe(footer, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
  }
}
